import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import DrawRectangle from 'mapbox-gl-draw-rectangle-mode';
import { Sidebar } from 'primereact/sidebar';
import { InputSwitch } from 'primereact/inputswitch';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';

import { toast, registerToast } from '../MapboxMap/toastService';
import { circleTwoClickMode, markerCircleMode, DrawExtraControls, makeCircleFeature, drawStyles, restoreDragPan } from '../MapboxMap/drawModes';
import { POINT_OUTSIDE_OPACITY, markFeaturesInsideShapes } from '../MapboxMap/insideShape';
import { RulerTool, RULER_LAYERS } from '../MapboxMap/rulerTool';
import DrawnShapesList from '../MapboxMap/DrawnShapesList';
import ShapeAttributesEditor from '../MapboxMap/ShapeAttributesEditor';
import RaioDeslocamentoModal from '../MapboxMap/RaioDeslocamentoModal';
// Features compartilhadas com o mapa Mapbox principal.
import { LAYER_LOADING_INITIAL_STATE } from '../MapboxMap/features/layerLoading';
import LayerLoadingIndicator from '../MapboxMap/features/LayerLoadingIndicator';
import * as zoneamento from '../MapboxMap/features/zoneamentoFeature';
import * as datalayer from '../MapboxMap/features/datalayer/datalayerFeature';
import SociodemografiaTools from '../MapboxMap/features/datalayer/SociodemografiaTools';
import * as mapCapture from '../MapboxMap/features/mapCapture';
import * as isochrone from '../MapboxMap/features/isochroneFeature';
import { syncAddressMarker, removeAddressMarker } from '../MapboxMap/features/addressMarker';

import EmpreendimentoServiceCrawler from '../../services/empreendimentosServicesCrawler';
import geolocationService from '../../services/geolocationService';
import KmlService from '../../services/KmlService';
import ModalPolosGeradores from '../ModalPolosGeradores';
import ModalSalvarProjeto from '../Projeto/ModalSalvarProjeto';
import MapCapture from '../MapCapture/MapCapture';
import LegendaDataLayer from '../LegendaDatalayer/Legenda';
import RendaIbgeLegend from '../RendaIbgeLegend';
import ModalLegendaRendaIbge from '../ModalLegendaRendaIbge/ModalLegendaRendaIbge';
import LinkagesDataDictionaryModal from '../LinkagesDataModal/LinkagesDataDictionaryModal';
import LinkagesDataTableModal from '../LinkagesDataModal/LinkagesDataTableModal';
import Legenda from '../MapaRevendaLocacao/Legenda';
import tipos from '../MapaRevendaLocacao/tipos';
import { estados } from '../../util/estados';
import colors from '../../util/colors';
import getBrowserGeolocation from '../../util/geolocation';

import FormFiltersRevenda from './FormFiltersRevenda';
import ResultsPanel from './ResultsPanel';
import ImovelModal from './ImovelModal';
import '../mapa.css';
import './MapaRevendaLocacaoMapbox.css';

const SOURCE_ID = 'imoveis';
const BAIRROS_SOURCE_ID = 'bairros';
const CLUSTER_LAYERS = ['clusters', 'cluster-count', 'unclustered-point'];
const EMPTY_FC = { type: 'FeatureCollection', features: [] };
const DEFAULT_ZOOM = 13;
// Alinhado ao gate de marcadores: ao selecionar cidade/endereço/estado, o zoom já
// fica no nível em que os marcadores do trecho visível são carregados.
const SELECTED_ZOOM = 13;
// Debounce do recarregamento de clusters ao mover/dar zoom no mapa.
const MOVE_DEBOUNCE_MS = 400;
// Grid mínimo do servidor (graus) acima do CLUSTER_MAX_ZOOM: só imóveis dentro
// dessa distância (~3 m, mesmo endereço/prédio) permanecem agrupados ao aproximar.
// Um cluster com espalhamento maior que isso ainda se separa dando zoom.
const EXACT_GRID_DEG = 0.00003;
// Espelha AmostrasClusterService::CLUSTER_MAX_ZOOM: acima desse zoom o servidor já
// usa o grid mínimo, e a resposta já é praticamente um marcador por imóvel — ou
// seja, o mapa já opera desagrupado a partir do zoom 16, sem pedir nada a mais.
const CLUSTER_MAX_ZOOM = 15;
// Zoom enviado para forçar o grid mínimo em qualquer zoom real. 22 é o máximo
// aceito por ImoveisClustersRequest e continua valendo se CLUSTER_MAX_ZOOM subir.
const ZOOM_GRID_EXATO = 22;
// Mesma escala do mapa Mapbox principal (MapboxMap.MARKER_ICON_SIZE): os PNGs de
// marcador são os mesmos 20x34px em /img/markers/, então o valor precisa ser
// idêntico para os pontos saírem do mesmo tamanho nos dois mapas.
const MARKER_ICON_SIZE = 0.95;

/**
 * Escapa texto vindo da API externa do MR Crawler antes de concatenar no HTML do
 * popup — mesma função de MapboxMap.jsx (duplicada aqui pelo mesmo motivo de
 * MARKER_ICON_SIZE: evitar acoplar os dois componentes por um export).
 */
const escapeHtml = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    })[c]);

const MAP_STYLE_STORAGE_KEY = 'geobrain.mrl-mapbox.style';
/*
 * A chave `light` é o mapa de ruas padrão e vem de antes de existir a opção `claro`
 * (light-v11, o estilo pálido). Os nomes se parecem, mas são estilos diferentes —
 * `light` não foi renomeada porque é o valor já gravado no localStorage dos usuários.
 * Mesmas opções do mapa Mapbox principal.
 */
const MAP_STYLES = {
    light:     'mapbox://styles/mapbox/streets-v12',
    dark:      'mapbox://styles/mapbox/dark-v11',
    satellite: 'mapbox://styles/mapbox/satellite-streets-v12',
    claro:     'mapbox://styles/mapbox/light-v11',
};
const MAP_STYLE_OPTIONS = [
    { value: 'light',     label: 'Ruas' },
    { value: 'claro',     label: 'Claro' },
    { value: 'dark',      label: 'Escuro' },
    { value: 'satellite', label: 'Satélite' },
];
const DEFAULT_MAP_STYLE = 'light';
const readStoredMapStyle = () => {
    try {
        const v = localStorage.getItem(MAP_STYLE_STORAGE_KEY);
        return MAP_STYLES[v] ? v : DEFAULT_MAP_STYLE;
    } catch {
        return DEFAULT_MAP_STYLE;
    }
};

// Permissão `ver-zoneamento`, injetada no page load pela blade do mapa.
const CAN_VER_ZONEAMENTO = zoneamento.CAN_VER_ZONEAMENTO;

// Cidades com limites de bairros (GeoJSON) disponíveis, no formato "UF-Cidade".
const BAIRROS_CITIES = new Set(window.bairrosCities || []);

// Ícones dos marcadores por tipo de imóvel × tipo de negócio (reuso do mapeamento
// do mapa Google, MapaRevendaLocacao/tipos.js). Cada PNG é carregado sob um nome
// derivado do próprio arquivo (ex.: /img/markers/apartamentos.png → "apartamentos").
const ICON_GROUPS = ['apartamentosTypes', 'casasTypes', 'ruralTypes', 'terrenosTypes', 'industrialTypes', 'comercialTypes'];
const iconNameFromUrl = (url) => url.split('/').pop().replace('.png', '');
const MARKER_ICONS = (() => {
    const map = new Map();
    ICON_GROUPS.forEach((g) => ['venda', 'aluguel'].forEach((neg) => {
        const url = tipos[g].icons[neg];
        map.set(iconNameFromUrl(url), url);
    }));
    return [...map.entries()].map(([name, url]) => ({ name, url }));
})();
// Índice tipo → { venda, aluguel } de nomes de ícone, montado uma vez. Evita
// varrer os 6 grupos (com array.includes) por feature — decisivo para cidades
// grandes (ex.: Curitiba, ~30 mil imóveis).
const TIPO_TO_ICONS = (() => {
    const map = new Map();
    ICON_GROUPS.forEach((g) => {
        const venda = iconNameFromUrl(tipos[g].icons.venda);
        const aluguel = iconNameFromUrl(tipos[g].icons.aluguel);
        tipos[g].types.forEach((t) => { if (!map.has(t)) map.set(t, { venda, aluguel }); });
    });
    return map;
})();
const getMarkerIconName = (tipo, isVenda) => {
    const entry = tipo ? TIPO_TO_ICONS.get(tipo) : null;
    return entry ? (isVenda ? entry.venda : entry.aluguel) : null;
};

const GOOGLE_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
const GOOGLE_SCRIPT_ID = 'google-maps-places-script';
function loadGoogleMapsPlaces() {
    if (window.google?.maps?.places) return Promise.resolve();
    if (document.getElementById(GOOGLE_SCRIPT_ID)) {
        return new Promise((resolve) => {
            document.getElementById(GOOGLE_SCRIPT_ID).addEventListener('load', resolve, { once: true });
        });
    }
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.id = GOOGLE_SCRIPT_ID;
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=places&language=pt-BR&region=BR`;
        script.async = true;
        script.defer = true;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
    });
}

const EMPTY_FILTERS = {
    projeto: null,
    tipo_negocio: 'Venda',
    tipo_empreendimento: [],
    estadosSelecionados: null,
    estado: [],
    cidade: [],
    bairros: [],
    bairrosOptions: [],
    area_maior_que: null,
    area_menor_que: null,
    valor_maior_que: null,
    valor_menor_que: null,
    valor_metro_maior_que: null,
    valor_metro_menor_que: null,
    quartos: [],
    vagas: [],
};

class MapaRevendaLocacaoMapbox extends Component {
    state = {
        left_side_bar_visible: true,
        right_panel_visible: false,
        active_tab: 'filtros',
        mapStyle: readStoredMapStyle(),
        loading: false,
        loadingClusters: false,
        loadingKml: false,
        showAlertCity: false,
        totalEmpreendimentos: 0,
        showEmpreendimentos: true,
        // Agrupamento dos marcadores — ligado por padrão, como no mapa principal.
        // Aqui quem agrupa é o servidor (grid PostGIS por zoom), então desligar
        // significa pedir o grid mínimo; ver loadClusters.
        agruparMarcadores: true,
        showBairros: false,
        bairrosAvailable: false,
        drawnShapes: [],
        searchAreas: [],
        // Filtros (componente controlado)
        filters: { ...EMPTY_FILTERS },
        projeto_name: [{ value: null, label: 'Selecione' }],
        // Detalhe do imóvel
        showImovelModal: false,
        imovelDetail: null,
        loadingImovel: false,
        // Editor de atributos de forma
        shapeEditorOpen: false,
        shapeEditing: null,
        // Salvar projeto (nome + compartilhamento, igual ao mapa Mapbox principal)
        modalSalvarProjetoOpen: false,
        projectName: '',
        projectShareUsers: [],
        projectErrors: {},
        savingProject: false,
        // Zoneamento, dados censitários, captura de tela e isócrona: features
        // compartilhadas (ver MapboxMap/features/).
        ...zoneamento.ZONEAMENTO_INITIAL_STATE,
        ...datalayer.DATALAYER_INITIAL_STATE,
        ...mapCapture.MAP_CAPTURE_INITIAL_STATE,
        ...isochrone.ISOCHRONE_INITIAL_STATE,
        ...LAYER_LOADING_INITIAL_STATE,
        // Polos geradores
        show_modal_polos_geradores: false,
        polos_geradores_current_showing: [],
        polos_geradores_current_grouped: [],
        polos_geradores_current_cache_key: '',
    };

    // Objetos do mapa como instance fields (fora do state para não re-renderizar).
    containerRef = React.createRef();
    searchInputRef = React.createRef();
    kmlInputRef = React.createRef();
    toastRef = React.createRef();
    map = null;
    draw = null;
    drawExtras = null;
    ruler = null;
    // Marcador do endereço buscado (só para ponto específico, não para cidade).
    addressMarker = null;
    allFeatures = [];
    currentGeojson = null;
    bairrosCache = {};
    authUserCities = [];
    _hidingIds = new Set();
    // Contexto da busca ativa, usado para recarregar os marcadores por viewport.
    // { mode: 'cidade'|'estado'|'shapes', state, cidadeArea } — null quando não há busca.
    searchContext = null;
    markerAbort = null;      // AbortController da busca de clusters em voo (cancela stale)
    moveTimer = null;        // debounce do moveend
    lastTileKey = null;      // última chave de tile+zoom carregada (evita refetch no mesmo tile)
    _clusterEventsBound = false; // ver bindClusterEvents
    // Popup de hover dos marcadores. Mesmo visual do mapa principal (balão escuro,
    // título + linha de atributo — ver .popup-mapa em mapa.css); é um só,
    // reaproveitado, e por isso vive aqui e não dentro do handler.
    hoverPopup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
        offset: 12,
        maxWidth: '300px',
        className: 'popup-mapa',
    });

    componentDidMount() {
        registerToast(this.toastRef);

        // Campos de instância das features compartilhadas (serviço de zoneamento,
        // caches das camadas). Ver MapboxMap/features/.
        zoneamento.initZoneamento(this);
        datalayer.initDatalayer(this);

        const apiKey = window.mapboxApiKey;
        if (!apiKey || !this.containerRef.current) {
            if (!apiKey) console.error('[MapaRevendaLocacaoMapbox] window.mapboxApiKey ausente');
            return;
        }

        mapboxgl.accessToken = apiKey;
        this.map = new mapboxgl.Map({
            container: this.containerRef.current,
            style: MAP_STYLES[this.state.mapStyle],
            center: [-49.1929618, -25.551136],
            zoom: DEFAULT_ZOOM,
            preserveDrawingBuffer: true,
        });

        this.map.on('error', (e) => console.error('[MapaRevendaLocacaoMapbox] erro:', e.error));
        this.map.addControl(new mapboxgl.NavigationControl(), 'top-right');
        this.map.addControl(new mapboxgl.FullscreenControl(), 'top-right');
        this.map.addControl(
            new mapboxgl.GeolocateControl({ positionOptions: { enableHighAccuracy: true }, trackUserLocation: true }),
            'top-right'
        );

        this.setupDrawTools();
        this.map.on('load', () => this.initializeLayers());
        // Recarrega os marcadores do trecho visível ao mover/dar zoom (só em busca por cidade).
        this.map.on('moveend', this.handleMoveEnd);

        this.centerOnBrowserLocation();
        this.setupGooglePlaces();
        this.fetchUserCities();
        this.getProjects();
        this.loadSociodemografiaPermissions();
    }

    componentWillUnmount() {
        clearTimeout(this.moveTimer);
        this.markerAbort?.abort();
        // Antes do map.remove(): a régua escuta o teclado no document.
        this.ruler?.destroy();
        if (this.map) this.map.remove();
    }

    centerOnBrowserLocation = async () => {
        try {
            const pos = await getBrowserGeolocation();
            if (pos?.lat && pos?.lng) this.map?.flyTo({ center: [pos.lng, pos.lat], zoom: DEFAULT_ZOOM });
        } catch { /* mantém o centro padrão */ }
    };

    fetchUserCities = async () => {
        try {
            const res = await fetch(route('web.auth_user_cities'));
            this.authUserCities = await res.json();
        } catch (e) {
            console.error('[MapaRevendaLocacaoMapbox] erro ao buscar cidades autorizadas:', e);
        }
    };

    // ===== Camadas =====
    initializeLayers = async () => {
        await this.loadMarkerImages();
        this.setupBairrosLayers();
        // Overlays antes dos marcadores: o Mapbox desenha na ordem de inserção e o
        // preenchimento translúcido das camadas apagaria os pins.
        zoneamento.setupZoneamentoLayers(this);
        datalayer.setupDatalayerLayers(this);
        this.setupClusterLayers();

        if (this.currentGeojson) {
            this.map.getSource(SOURCE_ID)?.setData(this.currentGeojson);
        }
        if (this.state.showBairros) {
            this.renderBairros(this.state.filters.cidade);
        }
        if (!this.state.showEmpreendimentos) {
            this.setEmpreendimentosVisibility(false);
        }
        // A troca de estilo descarta fontes e camadas: reaplica o que estiver ativo.
        zoneamento.reapplyZoneamento(this);
        datalayer.reapplyDatalayer(this);
        this.ruler?.reapplyAfterStyleChange();

        this.reordenarCamadas();
        // Na troca de estilo o MapboxDraw recria as próprias camadas por conta própria,
        // e nem sempre antes daqui — o `idle` garante um reempilhamento com elas presentes.
        this.map.once('idle', this.reordenarCamadas);
    };

    /**
     * Reempilha as camadas próprias. Ordem final: dados (zoneamento/censitários,
     * bairros) → desenhos → marcadores → régua. Os marcadores voltam ao topo porque o
     * preenchimento das formas é translúcido e os apagaria; a régua fica sobre tudo.
     */
    reordenarCamadas = () => {
        if (!this.map?.getStyle()) return;

        const camadasDoDraw = this.map.getStyle().layers
            .map((layer) => layer.id)
            .filter((id) => id.startsWith('gl-draw'));

        [...camadasDoDraw, ...CLUSTER_LAYERS, ...RULER_LAYERS].forEach((id) => {
            if (this.map.getLayer(id)) this.map.moveLayer(id);
        });
    };

    loadMarkerImages = () =>
        Promise.all(
            MARKER_ICONS.map(({ name, url }) =>
                new Promise((resolve) => {
                    if (this.map.hasImage(name)) return resolve();
                    this.map.loadImage(url, (err, image) => {
                        if (err || !image) return resolve();
                        if (!this.map.hasImage(name)) this.map.addImage(name, image);
                        resolve();
                    });
                })
            )
        );

    setupBairrosLayers = () => {
        if (this.map.getSource(BAIRROS_SOURCE_ID)) return;
        this.map.addSource(BAIRROS_SOURCE_ID, { type: 'geojson', data: EMPTY_FC });
        this.map.addLayer({
            id: 'bairros-fill', type: 'fill', source: BAIRROS_SOURCE_ID,
            paint: { 'fill-color': '#0d6efd', 'fill-opacity': 0.08 },
        });
        this.map.addLayer({
            id: 'bairros-outline', type: 'line', source: BAIRROS_SOURCE_ID,
            paint: { 'line-color': '#0d6efd', 'line-width': 1.5 },
        });
        this.map.addLayer({
            id: 'bairros-label', type: 'symbol', source: BAIRROS_SOURCE_ID,
            layout: { 'text-field': ['get', 'name'], 'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'], 'text-size': 11 },
            paint: { 'text-color': '#0a4dad', 'text-halo-color': '#ffffff', 'text-halo-width': 1.2 },
        });
    };

    // Fonte NÃO clusterizada no cliente: o servidor (PostGIS) já entrega clusters
    // (total>1) e pontos individuais (total==1) para o zoom/área atuais. As camadas
    // se distinguem pela propriedade `total`.
    setupClusterLayers = () => {
        if (this.map.getSource(SOURCE_ID)) return;

        this.map.addSource(SOURCE_ID, { type: 'geojson', data: EMPTY_FC });

        this.map.addLayer({
            id: 'clusters', type: 'circle', source: SOURCE_ID, filter: ['>', ['get', 'total'], 1],
            paint: {
                'circle-color': ['step', ['get', 'total'], '#51bbd6', 50, '#f1f075', 200, '#f28cb1'],
                'circle-radius': ['step', ['get', 'total'], 16, 50, 22, 200, 28, 1000, 34],
                'circle-stroke-width': 2, 'circle-stroke-color': '#ffffff',
                // Fora das formas desenhadas o marcador continua na tela, esmaecido.
                'circle-opacity': POINT_OUTSIDE_OPACITY,
                'circle-stroke-opacity': POINT_OUTSIDE_OPACITY,
            },
        });
        this.map.addLayer({
            id: 'cluster-count', type: 'symbol', source: SOURCE_ID, filter: ['>', ['get', 'total'], 1],
            layout: {
                'text-field': ['to-string', ['get', 'total']],
                'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                'text-size': 12,
            },
            paint: { 'text-opacity': POINT_OUTSIDE_OPACITY },
        });
        this.map.addLayer({
            id: 'unclustered-point', type: 'symbol', source: SOURCE_ID, filter: ['==', ['get', 'total'], 1],
            layout: { 'icon-image': ['get', 'icon'], 'icon-size': MARKER_ICON_SIZE, 'icon-allow-overlap': true, 'icon-anchor': 'bottom' },
            paint: { 'icon-opacity': POINT_OUTSIDE_OPACITY },
        });

        this.bindClusterEvents();
    };

    /**
     * Eventos das camadas de marcador. Fora de `setupClusterLayers` e com guarda,
     * porque aquela função roda de novo a cada troca de mapa base (o `setStyle`
     * descarta as sources, liberando o guard dela) — e o Mapbox não remove os
     * listeners junto com o estilo: eles vivem no Map e são delegados por id de
     * camada. Registrados ali, cada troca de estilo somava mais um conjunto, e um
     * clique num imóvel abria o modal tantas vezes quantas o estilo tivesse mudado.
     * Mesmo padrão de MapboxMap.bindClusterEvents.
     */
    bindClusterEvents = () => {
        if (this._clusterEventsBound) return;
        this._clusterEventsBound = true;

        // Clique num cluster: se os imóveis se separam ao aproximar, dá zoom (o
        // servidor re-clusteriza). Se são co-localizados (mesmo prédio → spread ~0),
        // aproximar não adianta: abre a lista dos imóveis do ponto.
        this.map.on('click', 'clusters', (e) => {
            const f = e.features?.[0];
            if (!f) return;
            const z = this.map.getZoom();
            const spread = f.properties.spread; // extensão em graus (undefined em cache antigo)
            // Não separa em nenhum zoom quando os pontos estão praticamente no mesmo
            // ponto (mesmo endereço/prédio) — cabem no grid mínimo do servidor.
            const naoSepara = spread != null && Number(spread) <= EXACT_GRID_DEG;
            if (z >= 18 || naoSepara) {
                this.openClusterList(f.geometry.coordinates, spread);
            } else {
                this.map.easeTo({ center: f.geometry.coordinates, zoom: Math.min(z + 2, 18) });
            }
        });

        const hoverPopup = this.hoverPopup;
        this.map.on('mouseenter', 'unclustered-point', (e) => {
            this.map.getCanvas().style.cursor = 'pointer';
            const p = e.features[0].properties;
            const address = [p.log, p.bai, p.cidade].filter((x) => x && String(x).length).join(', ');
            const html = `<div class="popup-title">${escapeHtml(p.anu || 'Anunciante não informado')}</div>`
                + `<div class="popup-row">${escapeHtml(address || 'Endereço não disponível')}</div>`;
            hoverPopup
                .setLngLat(e.features[0].geometry.coordinates.slice())
                .setHTML(html)
                .addTo(this.map);
        });
        this.map.on('mouseleave', 'unclustered-point', () => {
            this.map.getCanvas().style.cursor = '';
            hoverPopup.remove();
        });
        this.map.on('click', 'unclustered-point', (e) => {
            const f = e.features?.[0];
            if (!f) return;
            hoverPopup.remove();
            this.openImovelModal(f.properties?.id);
        });
        this.map.on('mouseenter', 'clusters', () => { this.map.getCanvas().style.cursor = 'pointer'; });
        this.map.on('mouseleave', 'clusters', () => { this.map.getCanvas().style.cursor = ''; });
    };

    setEmpreendimentosVisibility = (visible) => {
        const v = visible ? 'visible' : 'none';
        CLUSTER_LAYERS.forEach((id) => this.map?.getLayer(id) && this.map.setLayoutProperty(id, 'visibility', v));
    };

    toggleEmpreendimentos = (enabled) => {
        this.setState({ showEmpreendimentos: enabled });
        this.setEmpreendimentosVisibility(enabled);
    };

    /**
     * Liga/desliga o agrupamento dos marcadores. Diferente do mapa Mapbox principal,
     * aqui quem agrupa é o SERVIDOR (grid PostGIS derivado do zoom) — a source nunca
     * usou a opção `cluster` do Mapbox. Não há source nem camadas a recriar: basta
     * refazer a consulta pedindo o grid adequado (ver loadClusters).
     */
    toggleAgruparMarcadores = (enabled) => {
        this.setState({ agruparMarcadores: enabled }, () => {
            // O modo faz parte da chave do tile: sem zerar, o dedupe engoliria a troca.
            this.lastTileKey = null;
            this.loadClusters();
        });
    };

    // ===== Estilo do mapa (Mapa base) =====
    setMapStyle = (style) => {
        if (!MAP_STYLES[style] || style === this.state.mapStyle) return;
        this.setState({ mapStyle: style });
        try { localStorage.setItem(MAP_STYLE_STORAGE_KEY, style); } catch { /* localStorage indisponível */ }

        const drawn = this.draw?.getAll();
        this.map.setStyle(MAP_STYLES[style]);
        this.map.once('style.load', () => {
            this.initializeLayers();
            if (drawn?.features?.length) this.draw.set(drawn);
        });
    };

    // ===== Ferramentas de desenho =====
    setupDrawTools = () => {
        this.draw = new MapboxDraw({
            displayControlsDefault: false,
            controls: { polygon: true, trash: true },
            userProperties: true,
            styles: drawStyles,
            modes: {
                ...MapboxDraw.modes,
                draw_rectangle: DrawRectangle,
                draw_circle_two_click: circleTwoClickMode,
                draw_marker_circle: markerCircleMode,
            },
        });
        this.map.addControl(this.draw, 'top-left');

        // A régua se desliga sozinha no Esc, então o realce do botão vem do callback
        // e não do ponto onde ela foi ligada.
        this.ruler = new RulerTool({
            map: this.map,
            onChange: ({ active }) => this.drawExtras?.setRulerActive(active),
        });

        this.drawExtras = new DrawExtraControls({
            draw: this.draw,
            onIsochronePick: this.toggleIsochronePick,
            onRuler: this.toggleRuler,
            onModeChange: this.handleDrawModeChange,
        });
        this.map.addControl(this.drawExtras, 'top-left');

        /*
         * Os dois caminhos de troca de modo, que se complementam:
         *  - acionar uma ferramenta passa por DrawExtraControls.onModeChange, porque
         *    `draw.changeMode()` é API pública e roda silenciosa (suppressAPIEvents);
         *  - sair do modo (desenho concluído, Esc) é troca interna da lib e chega aqui.
         */
        this.map.on('draw.modechange', ({ mode }) => this.handleDrawModeChange(mode));

        this.map.on('draw.create', this.handleDrawCreate);
        this.map.on('draw.update', this.handleDrawUpdate);
        this.map.on('draw.delete', this.handleDrawDelete);

        // Rede de segurança: os eventos marcam o fim de uma interação com as formas,
        // então é seguro devolver o arrasto ao mapa (ver restoreDragPan).
        ['draw.create', 'draw.update', 'draw.delete', 'draw.modechange'].forEach(
            (evento) => this.map.on(evento, this.restoreDragPan)
        );
    };

    handleDrawCreate = (e) => {
        const feature = e.features?.[0];
        if (!feature || (feature.geometry?.type !== 'Polygon' && feature.geometry?.type !== 'MultiPolygon')) return;

        const shapeType = feature.properties?.shapeType;
        const typeLabels = { circle: 'Círculo', marker_circle: 'Círculo', rectangle: 'Retângulo' };
        const type = typeLabels[shapeType] || 'Polígono';
        const idx = this.state.drawnShapes.length;
        const color = colors[idx % colors.length];
        const areaId = `drawn-${Date.now()}`;

        const styleAttrs = { fillColor: color, fillOpacity: 0.3, strokeColor: '#000000', strokeWeight: 3 };
        Object.entries(styleAttrs).forEach(([k, v]) => this.draw.setFeatureProperty(feature.id, k, v));

        const shape = {
            drawId: feature.id,
            areaId,
            type,
            name: `${type} ${idx + 1}`,
            color,
            visible: true,
            feature: this.draw.get(feature.id) || feature,
            center: feature.properties?.center || null,
            radiusMeters: feature.properties?.radiusMeters || null,
            ...styleAttrs,
        };

        this.setState((prev) => ({ drawnShapes: [...prev.drawnShapes, shape] }), () => this.syncShapes());
    };

    handleDrawUpdate = (e) => {
        (e.features || []).forEach((feat) => {
            this.setState((prev) => ({
                drawnShapes: prev.drawnShapes.map((s) => s.drawId === feat.id
                    ? { ...s, feature: feat, center: feat.properties?.center || s.center, radiusMeters: feat.properties?.radiusMeters || s.radiusMeters }
                    : s),
            }));
        });
        this.syncShapes();
    };

    handleDrawDelete = (e) => {
        const deletedIds = (e.features || [])
            .map((f) => f.id)
            .filter((id) => {
                if (this._hidingIds.has(id)) { this._hidingIds.delete(id); return false; }
                return true;
            });
        if (deletedIds.length === 0) return;

        this.setState((prev) => {
            const removedAreaIds = prev.drawnShapes.filter((s) => deletedIds.includes(s.drawId)).map((s) => s.areaId);
            return {
                drawnShapes: prev.drawnShapes.filter((s) => !deletedIds.includes(s.drawId)),
                searchAreas: prev.searchAreas.filter((a) => !removedAreaIds.includes(a.id)),
            };
        }, () => this.syncShapes());
    };

    // ===== Lista de formas (aba Ferramentas) =====
    updateShape = (drawId, patch) => this.setState((prev) => ({
        drawnShapes: prev.drawnShapes.map((s) => s.drawId === drawId ? { ...s, ...patch } : s),
    }));

    centerOnShape = (shape) => {
        const feat = shape.visible ? this.draw?.get(shape.drawId) : shape.feature;
        if (!feat?.geometry?.coordinates) return;
        const bounds = new mapboxgl.LngLatBounds();
        const walk = (coords) => Array.isArray(coords[0]) ? coords.forEach(walk) : bounds.extend(coords);
        walk(feat.geometry.coordinates);
        this.map.fitBounds(bounds, { padding: 60, duration: 600 });
    };

    toggleShapeVisibility = (shape) => {
        if (shape.visible) {
            const feat = this.draw?.get(shape.drawId) || shape.feature;
            this._hidingIds.add(shape.drawId);
            this.draw?.delete(shape.drawId);
            this.updateShape(shape.drawId, { visible: false, feature: feat });
        } else {
            const [id] = this.draw?.add(shape.feature) || [];
            this.updateShape(shape.drawId, { visible: true, drawId: id ?? shape.drawId });
        }
    };

    setShapeRadius = (shape, radiusMeters) => {
        if (!shape.center || !(radiusMeters > 0)) return;
        const shapeType = shape.feature?.properties?.shapeType || 'circle';
        const updated = makeCircleFeature(shape.center, radiusMeters, shapeType);
        updated.properties = {
            ...updated.properties,
            fillColor: shape.fillColor, fillOpacity: shape.fillOpacity,
            strokeColor: shape.strokeColor, strokeWeight: shape.strokeWeight,
        };
        updated.id = shape.drawId;
        if (shape.visible) this.draw?.add(updated);
        this.updateShape(shape.drawId, { radiusMeters, feature: updated });
        this.syncShapes();
    };

    /**
     * Reage à troca de modo de desenho: desenhar e medir são exclusivos, então entrar
     * num modo de desenho encerra a régua.
     */
    handleDrawModeChange = (mode) => {
        if (mode && mode !== 'simple_select') this.deactivateRuler();
    };

    /*
     * ===== Features compartilhadas com o mapa Mapbox principal =====
     *
     * A implementação vive em MapboxMap/features/. Os métodos abaixo são só a ponte:
     * as features recebem a instância do componente e usam o contrato
     * getCidadesSelecionadas()/getFormasDesenhadas().
     */

    /** @returns {Array<{value: string, label: string}>} cidades selecionadas no filtro. */
    getCidadesSelecionadas = () => this.state.filters.cidade || [];

    /** @returns {Array} features do MapboxDraw (formas desenhadas). */
    getFormasDesenhadas = () => this.draw?.getAll().features || [];

    /** Uma isócrona é uma área desenhada como as outras — recarrega os cards das formas. */
    buscarPelaAreaDesenhada = () => this.syncShapes();

    toggleRuler = () => isochrone.toggleRuler(this);
    deactivateRuler = () => isochrone.deactivateRuler(this);
    toggleIsochronePick = () => isochrone.toggleIsochronePick(this);
    handleIsochroneCenterPick = (e) => isochrone.handleIsochroneCenterPick(this, e);
    handleIsochronesReady = (isochrones, tipoLabel) => isochrone.handleIsochronesReady(this, isochrones, tipoLabel);

    printMap = () => mapCapture.printMap(this);
    closeMapCapture = () => mapCapture.closeMapCapture(this);

    toggleShowZoneamento = (enabled) => zoneamento.toggleShowZoneamento(this, enabled);
    refreshZoneamentoAvailability = (cidades) => zoneamento.refreshZoneamentoAvailability(this, cidades);

    loadSociodemografiaPermissions = () => datalayer.loadSociodemografiaPermissions(this);
    loadYearsOptionsForLinkagesDataLayer = (cidades) => datalayer.loadYearsOptionsForLinkagesDataLayer(this, cidades);
    overlaysForDatalayerTable = () => datalayer.overlaysForDatalayerTable(this);
    getColorRangeKey = () => datalayer.getColorRangeKey(this);
    buildSensoPayload = (field) => datalayer.buildSensoPayload(this, field);

    editShapeAttributes = (shape) => this.setState({ shapeEditing: shape, shapeEditorOpen: true });
    // Ver restoreDragPan em MapboxMap/drawModes.js: sem isso o mapa perde o
    // clique-e-arrasto de vez quando um diálogo engole o mouseup de um arraste.
    restoreDragPan = () => restoreDragPan(this.map);

    closeShapeEditor = () => {
        this.restoreDragPan();
        this.setState({ shapeEditorOpen: false, shapeEditing: null });
    };

    applyShapeAttributes = (shape, attrs) => {
        if (!shape) return;
        const base = (shape.visible && this.draw?.get(shape.drawId)) || shape.feature;
        const updated = {
            ...base, id: shape.drawId,
            properties: {
                ...base.properties,
                fillColor: attrs.fillColor, fillOpacity: attrs.fillOpacity,
                strokeColor: attrs.strokeColor, strokeWeight: attrs.strokeWeight,
            },
        };
        if (shape.type === 'Círculo' && attrs.radiusMeters > 0 && shape.center) {
            const st = updated.properties?.shapeType || 'circle';
            const geo = makeCircleFeature(shape.center, attrs.radiusMeters, st);
            updated.geometry = geo.geometry;
            updated.properties = { ...updated.properties, radiusMeters: attrs.radiusMeters };
        }
        if (shape.visible) {
            this.draw?.add(updated);
            // Antes do changeMode: o novo onSetup do simple_select congela o estado
            // atual do dragPan (ver restoreDragPan).
            this.restoreDragPan();
            this.draw?.changeMode('simple_select', { featureIds: [] });
        }
        const name = (attrs.name || '').trim() || shape.name;
        const cor = attrs.fillColor === 'transparent' ? attrs.strokeColor : attrs.fillColor;
        this.updateShape(shape.drawId, {
            name, color: cor, fillColor: attrs.fillColor, fillOpacity: attrs.fillOpacity,
            strokeColor: attrs.strokeColor, strokeWeight: attrs.strokeWeight,
            radiusMeters: shape.type === 'Círculo' && attrs.radiusMeters > 0 ? attrs.radiusMeters : shape.radiusMeters,
            feature: updated,
        });
        this.setState((prev) => ({
            searchAreas: prev.searchAreas.map((a) => a.id === shape.areaId ? { ...a, name, color: cor } : a),
        }));
        this.closeShapeEditor();
    };

    removeShape = (shape) => {
        if (shape.visible) this.draw?.delete(shape.drawId);
        this._hidingIds.delete(shape.drawId);
        this.setState((prev) => ({
            drawnShapes: prev.drawnShapes.filter((s) => s.drawId !== shape.drawId),
            searchAreas: prev.searchAreas.filter((a) => a.id !== shape.areaId),
        }), () => this.syncShapes());
    };

    // Remove um card do painel (botão X). Se for um card de forma, remove também a
    // forma do mapa; se for de cidade/estado, só tira o card (histórico do painel).
    removeArea = (id) => {
        const area = this.state.searchAreas.find((a) => a.id === id);
        if (area?.kind === 'shape') {
            const shape = this.state.drawnShapes.find((s) => s.areaId === id);
            if (shape) { this.removeShape(shape); return; }
        }
        this.setState((prev) => {
            const areas = prev.searchAreas.filter((a) => a.id !== id);
            return { searchAreas: areas, right_panel_visible: areas.length > 0 };
        }, () => this.restoreMapContext());
    };

    // Redetermina o que o mapa deve exibir após uma remoção (card ou forma): formas
    // visíveis restantes (se houver), senão o card de cidade/estado mais recente que
    // ainda estiver no painel, senão limpa o mapa. Sem isso, remover a área que
    // estava ativa no mapa deixava os clusters da área removida presos na tela em
    // vez de recarregar a área restante.
    restoreMapContext = () => {
        const shapes = this.state.drawnShapes.filter((s) => s.visible);
        if (shapes.length > 0) {
            this.searchShapes(shapes, this.crawlerState());
            return;
        }
        const locationArea = this.state.searchAreas.find((a) => a.kind === 'location');
        if (locationArea) {
            this.searchContext = {
                mode: locationArea.typeLabel === 'Estado' ? 'estado' : 'cidade',
                state: locationArea.crawlerState,
                shapes: [],
            };
            this.lastTileKey = null;
            this.loadClusters();
            return;
        }
        this.searchContext = null;
        this.markerAbort?.abort();
        this.lastTileKey = null;
        this.map?.getSource(SOURCE_ID)?.setData(EMPTY_FC);
    };

    // ===== Filtros =====
    handleFilterChange = (field, value) => {
        this.setState((prev) => ({ filters: { ...prev.filters, [field]: value } }));
    };

    handleToggleQuarto = (q) => {
        this.setState((prev) => {
            const quartos = prev.filters.quartos.includes(q)
                ? prev.filters.quartos.filter((x) => x !== q)
                : [...prev.filters.quartos, q];
            return { filters: { ...prev.filters, quartos } };
        });
    };

    // Vaga 4 representa "4 ou mais" → expande para 4..16 no payload (paridade Google).
    handleToggleVaga = (v) => {
        this.setState((prev) => {
            const has = prev.filters.vagas.includes(v);
            let vagas;
            if (has) {
                vagas = prev.filters.vagas.filter((x) => x !== v);
                if (v === 4) vagas = vagas.filter((x) => x < 4);
            } else {
                vagas = [...prev.filters.vagas, v];
                if (v === 4) vagas = [...new Set([...vagas, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16])];
            }
            return { filters: { ...prev.filters, vagas } };
        });
    };

    handleEstadoChange = async (uf) => {
        this.setState((prev) => ({
            filters: { ...prev.filters, estadosSelecionados: uf, estado: uf ? [uf] : [], cidade: [], bairros: [], bairrosOptions: [] },
        }));
        await this.centralizarMapaNaCapital(uf);
    };

    handleCidadeChange = async (option) => {
        if (!option) {
            this.setState((prev) => ({ filters: { ...prev.filters, cidade: [], bairros: [], bairrosOptions: [] } }));
            return;
        }
        const uf = option.value.split('-')[0].trim();
        const bairrosOptions = await this.fetchCrawlerBairros(option.label);
        this.setState((prev) => ({
            filters: { ...prev.filters, cidade: [option], estado: [uf], estadosSelecionados: uf, bairros: [], bairrosOptions: bairrosOptions || [] },
        }));
        this.refreshBairrosAvailability([option]);

        try {
            const address = `${option.label} ${uf} - Brasil`;
            const location = await geolocationService.location(address);
            if (location?.latitude && location?.longitude) {
                this.map.flyTo({ center: [location.longitude, location.latitude], zoom: SELECTED_ZOOM });
            }
        } catch { /* posicionamento é best-effort */ }
    };

    centralizarMapaNaCapital = async (uf) => {
        if (!this.map || !uf) return;
        try {
            const res = await axios.get(route('state.capital', { state: uf }));
            const { lat, lng } = res.data || {};
            if (lat == null || lng == null) return;
            this.map.flyTo({ center: [Number(lng), Number(lat)], zoom: SELECTED_ZOOM });
        } catch (e) {
            console.error('[MapaRevendaLocacaoMapbox] erro ao centralizar na capital:', e);
        }
    };

    fetchCrawlerBairros = async (cidade) => {
        try {
            const res = await axios.get('/bairros-crawler/json/' + cidade);
            return res.data || [];
        } catch (e) {
            console.error('[MapaRevendaLocacaoMapbox] erro ao buscar bairros:', e);
            return [];
        }
    };

    // ===== Motor de busca =====
    // Estado no formato que o serviço do MR Crawler espera (buildPayload).
    crawlerState = () => {
        const f = this.state.filters;
        return {
            tipo_empreendimento: f.tipo_empreendimento,
            padrao: [],
            area_maior_que: f.area_maior_que ?? '',
            area_menor_que: f.area_menor_que ?? '',
            quartos: f.quartos,
            vagas: f.vagas,
            tipo_negocio: f.tipo_negocio,
            valor_menor_que: f.valor_menor_que ?? '',
            valor_maior_que: f.valor_maior_que ?? '',
            valor_metro_maior_que: f.valor_metro_maior_que ?? '',
            valor_metro_menor_que: f.valor_metro_menor_que ?? '',
            cidade: f.cidade,
            estado: f.estado,
            bairros: f.bairros,
            get_meta_data: false,
        };
    };

    reloadEmpreendimentos = async () => {
        const shapes = this.state.drawnShapes.filter((s) => s.visible);
        const hasShapes = shapes.length > 0;
        const f = this.state.filters;
        const hasCity = f.cidade.length > 0 || f.estado.length > 0;

        if (!hasShapes && !hasCity) {
            toast.warn('Desenhe uma área no mapa ou selecione uma cidade.');
            this.setState({ showAlertCity: true });
            return;
        }

        this.setState({ loading: true, showAlertCity: false });
        const state = this.crawlerState();

        try {
            // O painel ACUMULA: uma nova busca entra no topo e as anteriores permanecem
            // (paridade com o mapa antigo).
            if (hasShapes) {
                this.searchShapes(shapes, state, true); // BUSCAR: recarrega todas as formas (filtros podem ter mudado)
            } else {
                // ===== Busca por cidade/estado ===== → novo card no topo (id único).
                const buscaPorEstado = f.cidade.length === 0 && f.estado.length > 0;
                const area = {
                    id: `loc-${Date.now()}`,
                    kind: 'location',
                    name: buscaPorEstado ? f.estado.join(', ') : (f.cidade[0]?.label || 'Cidade'),
                    type: 'cidade',
                    typeLabel: buscaPorEstado ? 'Estado' : 'Cidade',
                    color: colors[this.state.searchAreas.length % colors.length],
                    tipo_negocio: f.tipo_negocio,
                    count_buildings: 0,     // preenchido pela agregação (soma dos totais)
                    query_id: null,         // obtido sob demanda ao exportar (Excel)
                    crawlerState: state,    // usado na exportação lazy
                    loading: true,
                };
                this.searchContext = { mode: buscaPorEstado ? 'estado' : 'cidade', state, shapes: [] };
                this.setState((prev) => ({ searchAreas: [area, ...prev.searchAreas], right_panel_visible: true }));
                this.loadMetadataForAreas([area], [], false, state);
                this.lastTileKey = null;
                this.loadClusters();
            }

            if (this.state.showBairros) this.renderBairros(f.cidade);
            this.refreshBairrosAvailability(f.cidade);
            this.refreshZoneamentoAvailability(f.cidade);
            this.loadYearsOptionsForLinkagesDataLayer(f.cidade);
        } catch (e) {
            console.error('[MapaRevendaLocacaoMapbox] erro na busca:', e);
            toast.error('Erro ao carregar imóveis.');
        } finally {
            this.setState({ loading: false });
        }
    };

    // Sincroniza os cards das formas desenhadas (recentes no topo), preservando os
    // cards de cidade/estado já no painel; re-renderiza os clusters das formas.
    // `force`: recarrega os metadados de TODAS as formas (ex.: BUSCAR após mudar
    // filtros). Sem force (eventos de desenho), preserva os cards já carregados cuja
    // geometria não mudou — só carrega o novo/movido, evitando flicker.
    searchShapes = (shapes, state, force = false) => {
        const f = this.state.filters;
        const prevById = new Map(this.state.searchAreas.map((a) => [a.id, a]));

        const shapeAreas = shapes.map((shape) => {
            const geom = (this.draw.get(shape.drawId) || shape.feature).geometry;
            const prev = prevById.get(shape.areaId);
            const inalterada = prev && prev.kind === 'shape' && !prev.loading
                && JSON.stringify(prev.shapeGeometry) === JSON.stringify(geom);
            if (!force && inalterada) {
                return { ...prev, name: shape.name, color: shape.color, radius: shape.radiusMeters ?? null };
            }
            return {
                id: shape.areaId,
                kind: 'shape',
                name: shape.name,
                type: shape.type,
                typeLabel: shape.type,
                color: shape.color,
                tipo_negocio: f.tipo_negocio,
                count_buildings: 0,
                query_id: null,
                shapeGeometry: geom,
                loading: true,
                radius: shape.radiusMeters ?? null,
            };
        });

        this.searchContext = { mode: 'shapes', state, shapes };
        this.fitBoundsToShapes(shapes);
        this.setState((prev) => {
            const locations = prev.searchAreas.filter((a) => a.kind !== 'shape');
            return { searchAreas: [...[...shapeAreas].reverse(), ...locations], right_panel_visible: true };
        });

        const toLoad = shapeAreas.filter((a) => a.loading);
        if (toLoad.length > 0) this.loadMetadataForAreas(toLoad, shapes, true, state);
        this.lastTileKey = null;
        this.loadClusters();
    };

    // Reação a eventos de desenho (criar/mover/remover formas). NÃO dispara busca de
    // cidade: se não há mais formas, limpa só os cards de forma + clusters, mantendo
    // os cards de cidade/estado acumulados no painel.
    syncShapes = () => {
        const shapes = this.state.drawnShapes.filter((s) => s.visible);
        if (shapes.length === 0) {
            this.setState((prev) => {
                const locations = prev.searchAreas.filter((a) => a.kind !== 'shape');
                return { searchAreas: locations, right_panel_visible: locations.length > 0 };
            }, () => this.restoreMapContext());
            return;
        }
        this.searchShapes(shapes, this.crawlerState());
    };

    // ===== Clusterização no servidor =====
    // Recarrega os clusters ao mover/dar zoom. Vale para todos os modos: a consulta é
    // sempre o tile do viewport (ver loadClusters), então pan e zoom mudam o conjunto
    // exibido. `lastTileKey` evita refazer a requisição quando o tile não muda.
    handleMoveEnd = () => {
        if (!this.searchContext) return;
        clearTimeout(this.moveTimer);
        this.moveTimer = setTimeout(() => this.loadClusters(), MOVE_DEBOUNCE_MS);
    };

    // bbox do viewport alinhado a uma grade de tiles (em graus, dependente do zoom),
    // expandido para fora. Isso estabiliza o retângulo consultado: pans próximos e
    // usuários diferentes vendo a mesma região geram a MESMA requisição → cache do
    // servidor + possibilidade de pular refetch. Retorna o polígono e uma chave.
    tileBbox = (zoom) => {
        const b = this.map.getBounds();
        const tile = 360 / Math.pow(2, Math.max(1, zoom - 2)); // ~tamanho do tile em graus
        const floor = (v) => Math.floor(v / tile) * tile;
        const ceil = (v) => Math.ceil(v / tile) * tile;
        const w = floor(b.getWest()), s = floor(b.getSouth()), e = ceil(b.getEast()), n = ceil(b.getNorth());
        return {
            poly: { type: 'Polygon', coordinates: [[[w, s], [e, s], [e, n], [w, n], [w, s]]] },
            key: `${zoom}:${w.toFixed(4)}:${s.toFixed(4)}:${e.toFixed(4)}:${n.toFixed(4)}`,
        };
    };

    // Formas visíveis como features de polígono, para o recorte com turf.
    shapesFeatures = () => {
        const ctx = this.searchContext;
        if (ctx?.mode !== 'shapes') return [];
        return ctx.shapes.map((s) => this.draw?.get(s.drawId) || s.feature).filter(Boolean);
    };

    loadClusters = async () => {
        const ctx = this.searchContext;
        if (!ctx || !this.map?.getSource(SOURCE_ID)) return;

        const zoom = Math.round(this.map.getZoom());

        // A consulta é sempre o tile do viewport — inclusive com formas desenhadas, para
        // que os imóveis de fora sigam na tela (esmaecidos) e o usuário saiba onde vale
        // desenhar a próxima área. O recorte pelas formas é feito no cliente, marcando
        // `inside`; o total de cada card do painel vem da própria consulta por forma
        // (loadMetadataForAreas), então continua exato.
        const { poly, key } = this.tileBbox(zoom);
        // Com o agrupamento desligado, pede o grid mínimo do servidor — um marcador por
        // imóvel. Acima de CLUSTER_MAX_ZOOM o zoom real já produz esse mesmo grid, então
        // manter o zoom real ali evita refazer a busca por um resultado idêntico (e
        // reaproveita o cache do servidor).
        const desagrupar = !this.state.agruparMarcadores && zoom <= CLUSTER_MAX_ZOOM;
        const zoomConsulta = desagrupar ? ZOOM_GRID_EXATO : zoom;
        // O modo entra na chave: alternar o switch parado no mesmo tile precisa refazer
        // a busca, senão o dedupe abaixo engoliria a troca.
        const reqKey = `${desagrupar ? 'u' : 'g'}:${key}`;
        // Mesmo tile/zoom/modo da última carga → os dados já estão na tela; não refaz.
        if (reqKey === this.lastTileKey) return;
        this.lastTileKey = reqKey;

        this.markerAbort?.abort();
        const controller = new AbortController();
        this.markerAbort = controller;
        const signal = controller.signal;
        this.setState({ loadingClusters: true });

        try {
            const res = await EmpreendimentoServiceCrawler.getClusters(poly, zoomConsulta, ctx.state, { signal });
            // Identidade do controller, e não signal.aborted: cobre também o caso de uma
            // busca mais nova ter assumido o lugar desta.
            if (this.markerAbort !== controller) return;

            const features = this.buildClusterFeatures(res.data?.features || []);
            markFeaturesInsideShapes(features, this.shapesFeatures());
            this.currentGeojson = { type: 'FeatureCollection', features };
            this.map.getSource(SOURCE_ID)?.setData(this.currentGeojson);
        } catch (e) {
            if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED' || signal.aborted) return;
            console.error('[MapaRevendaLocacaoMapbox] erro ao carregar clusters:', e);
            // A chave é gravada antes da requisição; mantê-la após uma falha de rede
            // travaria o dedupe neste tile, e só um pan para fora permitiria retentar.
            if (this.lastTileKey === reqKey) this.lastTileKey = null;
        } finally {
            // Só apaga o loader se esta ainda for a última requisição (pan/zoom rápido
            // inicia outra antes desta terminar — a nova mantém o loader ligado).
            if (this.markerAbort === controller) this.setState({ loadingClusters: false });
        }
    };

    // Popup reutilizado com a lista de imóveis de um cluster co-localizado.
    clusterListPopup = new mapboxgl.Popup({ maxWidth: '340px', closeButton: true });

    // Abre a lista dos imóveis num ponto (cluster que não se separa por zoom).
    openClusterList = async (center, spread) => {
        const ctx = this.searchContext;
        if (!ctx) return;

        // bbox pequeno em volta do centro, cobrindo o espalhamento do cluster (+ margem).
        const half = (Number(spread) || 0) / 2 + 0.0004; // ~44 m de margem
        const [lng, lat] = center;
        const geo = {
            type: 'Polygon',
            coordinates: [[
                [lng - half, lat - half], [lng + half, lat - half],
                [lng + half, lat + half], [lng - half, lat + half], [lng - half, lat - half],
            ]],
        };

        this.setState({ loadingClusters: true });
        try {
            const res = await EmpreendimentoServiceCrawler.getPointsInArea(geo, ctx.state);
            const pts = res.data || [];
            if (pts.length === 0) return;

            const items = pts.map((p) => {
                const addr = [p.log, p.bai].filter(Boolean).join(', ');
                return `<button type="button" class="mrl-cluster-item" data-id="${p.id}">`
                    + `<span class="mrl-cluster-item-tipo">${p.tipo || 'Imóvel'}</span>`
                    + `<span class="mrl-cluster-item-sub">${[p.anu, addr].filter(Boolean).join(' — ') || 'Sem descrição'}</span>`
                    + `</button>`;
            }).join('');

            const html = `<div class="mrl-cluster-list">`
                + `<div class="mrl-cluster-list-header">${pts.length} imóvei${pts.length > 1 ? 's' : ''} neste ponto</div>`
                + `<div class="mrl-cluster-list-body">${items}</div></div>`;

            this.clusterListPopup.setLngLat(center).setHTML(html).addTo(this.map);
            this.clusterListPopup.getElement()
                .querySelectorAll('.mrl-cluster-item')
                .forEach((btn) => btn.addEventListener('click', () => {
                    this.clusterListPopup.remove();
                    this.openImovelModal(btn.dataset.id);
                }));
        } catch (e) {
            console.error('[MapaRevendaLocacaoMapbox] erro ao listar imóveis do cluster:', e);
            toast.error('Não foi possível listar os imóveis deste ponto.');
        } finally {
            this.setState({ loadingClusters: false });
        }
    };

    // Prepara as features vindas do servidor para renderização:
    // - clusters (total>1): passam direto (círculo + contagem).
    // - pontos (total==1): filtra por cidade autorizada e atribui o ícone por
    //   tipo × negócio (mutação in-place; sem ícone reconhecido → omitido).
    buildClusterFeatures = (features) => {
        const isVenda = this.state.filters.tipo_negocio.toLowerCase() === 'venda';
        const auth = this.authUserCities || [];
        const authSet = auth.length > 0 ? new Set(auth) : null;
        const out = [];
        for (const feat of features) {
            const p = feat.properties;
            if (!p) continue;
            if (p.total > 1) { out.push(feat); continue; }
            if (authSet && p.cidade && !authSet.has(p.cidade)) continue;
            const icon = getMarkerIconName(p.tipo, isVenda);
            if (!icon) continue;
            p.icon = icon;
            out.push(feat);
        }
        return out;
    };

    fitBoundsToShapes = (shapes) => {
        if (!this.map || !shapes.length) return;
        const bounds = new mapboxgl.LngLatBounds();
        const walk = (coords) => Array.isArray(coords[0]) ? coords.forEach(walk) : bounds.extend(coords);
        shapes.forEach((s) => {
            const feat = this.draw.get(s.drawId) || s.feature;
            if (feat?.geometry?.coordinates) walk(feat.geometry.coordinates);
        });
        if (!bounds.isEmpty()) this.map.fitBounds(bounds, { padding: 60, duration: 600, maxZoom: 16 });
    };

    updateArea = (id, patch) => this.setState((prev) => ({
        searchAreas: prev.searchAreas.map((a) => a.id === id ? { ...a, ...patch } : a),
    }));

    loadMetadataForAreas = async (areas, shapes, hasShapes, state) => {
        const totais = await Promise.all(areas.map(async (area) => {
            try {
                // Cidade/estado → agregação por cidade/estado; forma → por polígono.
                const geometry = area.shapeGeometry || null;
                const fetchAgr = (agr) => geometry
                    ? EmpreendimentoServiceCrawler.getMetadataWithinPolygon(geometry, state, agr)
                    : EmpreendimentoServiceCrawler.getMetadataWithinCidade(state, agr);

                const [m, mq, mp] = await Promise.all([
                    fetchAgr(['tipo']),
                    fetchAgr(['tipo', 'quartos']),
                    fetchAgr(['tipo', 'padrao']),
                ]);
                const metadata = m.data?.data ?? [];
                // O total da área é a soma dos totais da agregação (não baixamos as
                // features individuais — o mapa mostra clusters do servidor).
                const total = metadata.reduce((sum, row) => sum + Number(row.total || 0), 0);
                this.updateArea(area.id, {
                    metadata,
                    metadataQuartos: mq.data?.data ?? [],
                    metadataPadrao: mp.data?.data ?? [],
                    count_buildings: total,
                    loading: false,
                });
                return total;
            } catch (e) {
                console.error('[MapaRevendaLocacaoMapbox] erro ao carregar metadados da área:', e);
                this.updateArea(area.id, { loading: false });
                return null; // erro ≠ busca vazia
            }
        }));

        // Nenhuma das áreas buscadas trouxe imóveis: sem o aviso o usuário só vê o
        // mapa e o painel zerados, sem saber se a busca falhou ou não achou nada.
        if (totais.length > 0 && totais.every((t) => t === 0)) {
            toast.warn('Nenhum resultado com os parâmetros fornecidos.');
        }
    };

    // ===== Detalhe do imóvel =====
    // As sidebars são não-modais (ficam por cima do mapa e acima da máscara do
    // Dialog), então competem visualmente com o modal do imóvel: escondemos as duas
    // enquanto ele estiver aberto e devolvemos ao estado anterior ao fechar.
    sidebarsBeforeImovelModal = null;

    hideSidebarsForImovelModal = () => {
        this.sidebarsBeforeImovelModal = {
            left: this.state.left_side_bar_visible,
            right: this.state.right_panel_visible,
        };
        this.setState({ left_side_bar_visible: false, right_panel_visible: false });
    };

    restoreSidebarsAfterImovelModal = () => {
        const prev = this.sidebarsBeforeImovelModal;
        if (!prev) return;
        this.sidebarsBeforeImovelModal = null;
        this.setState({ left_side_bar_visible: prev.left, right_panel_visible: prev.right });
    };

    openImovelModal = async (id) => {
        if (!id || this.state.loadingImovel) return;
        this.hideSidebarsForImovelModal();
        this.setState({ showImovelModal: true, loadingImovel: true, imovelDetail: null });
        try {
            const data = await EmpreendimentoServiceCrawler.getEmpreendimento(id);
            // O crawler devolve o documento direto, mas há ambientes que ainda
            // envelopam em { data: {...} } (era o que o mapa antigo consumia).
            const imovel = data && data.titulo === undefined && data.data ? data.data : data;
            this.setState({ imovelDetail: imovel });
        } catch (e) {
            console.error('[MapaRevendaLocacaoMapbox] erro ao carregar detalhe:', e);
            toast.error('Não foi possível carregar os detalhes do imóvel.');
            this.setState({ showImovelModal: false });
            this.restoreSidebarsAfterImovelModal();
        } finally {
            this.setState({ loadingImovel: false });
        }
    };

    closeImovelModal = () => {
        this.restoreDragPan();
        this.setState({ showImovelModal: false, imovelDetail: null });
        this.restoreSidebarsAfterImovelModal();
    };

    // ===== Excel + Polos =====
    // O Excel precisa do query_id de uma consulta de LISTA (o crawler cacheia o
    // query builder sob esse id por 3h). Como o mapa só carrega clusters, o query_id
    // é obtido sob demanda no clique: cidade/estado via lista da cidade; forma via
    // lista dentro do polígono.
    handleExportExcel = async (area) => {
        try {
            let queryId = area.query_id;
            if (!queryId) {
                toast.info('Preparando a exportação…');
                const res = area.shapeGeometry
                    ? await EmpreendimentoServiceCrawler.getEmpreendimentosWithinPolygon(area.shapeGeometry, this.crawlerState())
                    : await EmpreendimentoServiceCrawler.getEmpreendimentoOnCidade(area.crawlerState);
                queryId = res.headers['x-query-id'];
            }
            if (!queryId) {
                toast.error('Não foi possível preparar a exportação desta área.');
                return;
            }
            const res = await EmpreendimentoServiceCrawler.exportExcel(queryId);
            toast.success(res.data?.message || 'Exportação solicitada.');
        } catch (e) {
            console.error('[MapaRevendaLocacaoMapbox] erro ao exportar Excel:', e);
            toast.error('Erro ao solicitar a exportação.');
        }
    };

    handleShowPolos = (area) => {
        const polos = area.polos_geradores || [];
        const agrupamentos = [];
        polos.forEach((polo) => {
            const category = polo.main_category || polo.categories?.[0];
            const found = agrupamentos.find((a) => a.category === category);
            if (found) found.count++;
            else agrupamentos.push({ category, count: 1 });
        });
        this.setState({
            polos_geradores_current_showing: polos,
            polos_geradores_current_grouped: agrupamentos,
            polos_geradores_current_cache_key: area.polos_geradores_cache_key || '',
            show_modal_polos_geradores: true,
        });
    };

    // ===== Limites de bairros =====
    handleToggleBairros = (enabled) => {
        this.setState({ showBairros: enabled });
        if (!this.map?.getSource(BAIRROS_SOURCE_ID)) return;
        if (!enabled) {
            this.map.getSource(BAIRROS_SOURCE_ID).setData(EMPTY_FC);
            return;
        }
        const cidades = this.state.filters.cidade;
        if (cidades.length === 0) toast.info('Selecione e busque uma cidade para ver os limites de bairros.');
        this.renderBairros(cidades);
    };

    renderBairros = async (cidades) => {
        if (!this.map?.getSource(BAIRROS_SOURCE_ID)) return;
        const groups = await Promise.all((cidades || []).map((c) => this.fetchBairrosGeojson(c.value)));
        this.map.getSource(BAIRROS_SOURCE_ID)?.setData({ type: 'FeatureCollection', features: groups.flat() });
    };

    fetchBairrosGeojson = async (value) => {
        if (this.bairrosCache[value]) return this.bairrosCache[value];
        const idx = value.indexOf('-');
        if (idx === -1) return [];
        const uf = value.slice(0, idx);
        const cidade = value.slice(idx + 1);
        try {
            const res = await axios.get(`/bairros/geojson/${encodeURIComponent(cidade)}/${encodeURIComponent(uf)}`);
            const features = res.data?.features || [];
            this.bairrosCache[value] = features;
            return features;
        } catch (e) {
            console.error('[MapaRevendaLocacaoMapbox] erro ao carregar limites de bairros:', e);
            return [];
        }
    };

    refreshBairrosAvailability = (cidades) => {
        const available = (cidades || []).some((c) => BAIRROS_CITIES.has(c.value));
        this.setState({ bairrosAvailable: available });
        if (!available && this.state.showBairros) {
            this.setState({ showBairros: false });
            this.map?.getSource(BAIRROS_SOURCE_ID)?.setData(EMPTY_FC);
        }
    };

    // ===== KML: exportação / importação =====
    handleDownloadKml = async () => {
        const features = this.draw?.getAll().features || [];
        const polygons = [];
        const circles = [];
        features.forEach((f) => {
            if (f.geometry?.type !== 'Polygon') return;
            const isCircle = ['circle', 'marker_circle'].includes(f.properties?.shapeType);
            if (isCircle && f.properties?.center && f.properties?.radiusMeters) {
                circles.push({ latitude: f.properties.center[1], longitude: f.properties.center[0], radius: f.properties.radiusMeters });
            } else {
                const ring = f.geometry.coordinates?.[0] || [];
                polygons.push(ring.map(([lng, lat]) => [lat, lng]));
            }
        });
        // Só o que está dentro das formas: os de fora estão na tela apenas como
        // referência visual e não fazem parte do recorte exportado.
        const exibidos = (this.currentGeojson?.features || []).filter((f) => f.properties?.inside !== false);
        const empreendimentos = exibidos.map((f) => {
            const p = f.properties || {};
            return {
                _id: p.id, nome: p.anu || '', tipo_empreendimento: p.tipo || '',
                endereco: p.log || '', bairro: p.bai || '', cidade: p.cidade || '', marker: p.icon || '',
                location: { coordinates: f.geometry?.coordinates || null },
            };
        });
        if (polygons.length === 0 && circles.length === 0 && empreendimentos.length === 0) {
            toast.info('Nada para exportar. Desenhe uma forma ou faça uma busca primeiro.');
            return;
        }
        try {
            this.setState({ loadingKml: true });
            await KmlService.download({ polygons, circles, empreendimentos });
        } catch (e) {
            console.error('[MapaRevendaLocacaoMapbox] erro ao exportar KML:', e);
            toast.error('Erro ao exportar o KML.');
        } finally {
            this.setState({ loadingKml: false });
        }
    };

    handleImportKmlFile = async (file) => {
        if (!file) return;
        const formData = new FormData();
        formData.append('file', file);
        try {
            this.setState({ loadingKml: true });
            const response = await axios.post(route('transform-kml'), formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            if (response.status !== 200 || !Array.isArray(response.data) || response.data.length === 0) {
                toast.warn('Nenhum polígono encontrado no arquivo.');
                return;
            }
            await this.addImportedPolygons(response.data);
            toast.success(`${response.data.length} forma(s) importada(s).`);
        } catch (e) {
            console.error('[MapaRevendaLocacaoMapbox] erro ao importar KML/KMZ:', e);
            toast.error('Erro ao importar o arquivo KML/KMZ.');
        } finally {
            this.setState({ loadingKml: false });
        }
    };

    addImportedPolygons = async (paths) => {
        if (!this.draw) return;
        const newShapes = [];
        const allBounds = new mapboxgl.LngLatBounds();
        let firstCenter = null;

        paths.forEach((path, i) => {
            if (!Array.isArray(path) || path.length < 3) return;
            const ring = path.map(([lat, lng]) => [lng, lat]);
            const first = ring[0];
            const last = ring[ring.length - 1];
            if (first[0] !== last[0] || first[1] !== last[1]) ring.push([...first]);

            const [drawId] = this.draw.add({ type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [ring] } });
            const idx = this.state.drawnShapes.length + newShapes.length;
            const color = colors[idx % colors.length];
            const areaId = `drawn-${Date.now()}-${i}`;
            const styleAttrs = { fillColor: color, fillOpacity: 0.3, strokeColor: '#000000', strokeWeight: 3 };
            Object.entries(styleAttrs).forEach(([k, v]) => this.draw.setFeatureProperty(drawId, k, v));

            const b = new mapboxgl.LngLatBounds();
            ring.forEach((c) => { b.extend(c); allBounds.extend(c); });
            if (!firstCenter) firstCenter = b.getCenter();

            newShapes.push({
                drawId, areaId, type: 'Polígono', name: `Polígono ${idx + 1}`, color, visible: true,
                feature: this.draw.get(drawId), center: null, radiusMeters: null, ...styleAttrs,
            });
        });

        if (newShapes.length === 0) { toast.warn('Nenhuma forma válida no arquivo.'); return; }

        // Detecta a UF pela geocodificação reversa do centróide da primeira forma.
        if (firstCenter) {
            try {
                const r = await geolocationService.reverse(firstCenter.lat, firstCenter.lng, null);
                if (r?.uf) {
                    this.setState((prev) => ({ filters: { ...prev.filters, estadosSelecionados: r.uf, estado: [r.uf] } }));
                }
            } catch { /* segue sem UF */ }
        }

        await new Promise((resolve) => this.setState((prev) => ({ drawnShapes: [...prev.drawnShapes, ...newShapes] }), resolve));
        if (!allBounds.isEmpty()) this.map.fitBounds(allBounds, { padding: 60, duration: 600 });
        this.reloadEmpreendimentos();
    };

    // ===== Projetos =====
    /**
     * Lista os projetos próprios e os compartilhados. Usa a rota do Mapbox, que
     * identifica o projeto por id — com compartilhamento o nome deixa de ser único
     * entre usuários (a rota legada, por nome, segue servindo o mapa Google).
     */
    getProjects = async () => {
        try {
            const res = await axios.get(route('api.projetos-revenda-locacao-mapbox'));
            const options = [{ value: null, label: 'Selecione' }];
            (res.data?.projects || []).forEach((projeto) => options.push({
                value: projeto.value,
                label: projeto.compartilhado ? `${projeto.label} (compartilhado)` : projeto.label,
            }));
            this.setState({ projeto_name: options });
        } catch (e) {
            console.error('[MapaRevendaLocacaoMapbox] erro ao listar projetos:', e);
        }
    };

    // Serializa as formas no formato legado (intercambiável com o mapa Google):
    // polígonos como caminhos [[lat,lng],...] e círculos como {latitude, longitude, radius}.
    serializeShapesForProject = () => {
        const polygons = [];
        const circles = [];
        this.state.drawnShapes.forEach((shape) => {
            const feature = this.draw?.get(shape.drawId) || shape.feature;
            if (!feature) return;
            const style = { color: shape.fillColor, fillColor: shape.fillColor, strokeColor: shape.strokeColor, fillOpacity: shape.fillOpacity, strokeWeight: shape.strokeWeight };
            if (['circle', 'marker_circle'].includes(feature.properties?.shapeType)) {
                const center = feature.properties.center || shape.center;
                const radius = feature.properties.radiusMeters || shape.radiusMeters;
                if (!center || !radius) return;
                circles.push({ latitude: center[1], longitude: center[0], radius, name: shape.name, type: shape.type, ...style });
                return;
            }
            if (feature.geometry?.type !== 'Polygon') return;
            const ring = feature.geometry.coordinates[0] || [];
            const path = ring.slice(0, -1).map(([lng, lat]) => [lat, lng]);
            if (path.length < 3) return;
            polygons.push({ name: shape.name, polygon: path, ...style });
        });
        return { polygons, circles };
    };

    createProjectPayload = () => {
        const f = this.state.filters;
        const { polygons, circles } = this.serializeShapesForProject();
        const center = this.map?.getCenter();
        const tipoNegocio = f.tipo_negocio === 'Aluguel'
            ? [{ label: 'Locação', value: 'Aluguel' }]
            : [{ label: 'Revenda', value: 'Venda' }];

        return {
            project_name: this.state.projectName,
            share_users: this.state.projectShareUsers,
            tipo_negocio: tipoNegocio,
            tipo_empreendimento: f.tipo_empreendimento,
            cidade: f.cidade,
            estado: f.estado,
            bairros: f.bairros,
            quartos: f.quartos,
            vagas: f.vagas,
            polygons,
            circles,
            area_maior_que: f.area_maior_que || 0,
            area_menor_que: f.area_menor_que || 0,
            valor_maior_que: f.valor_maior_que || 0,
            valor_menor_que: f.valor_menor_que || 0,
            valor_metro_maior_que: f.valor_metro_maior_que || 0,
            valor_metro_menor_que: f.valor_metro_menor_que || 0,
            currentPosition: { lat: center?.lat ?? null, lng: center?.lng ?? null },
            raio: 3000,
        };
    };

    saveProject = async () => {
        if (!this.state.projectName.trim()) { toast.warn('Informe um nome para o projeto.'); return; }
        this.setState({ savingProject: true });
        try {
            const res = await axios.post('/mapa-revenda-locacao/projeto', this.createProjectPayload());
            toast.success(res.data?.message || 'Projeto salvo.');
            this.setState({ modalSalvarProjetoOpen: false, projectName: '', projectShareUsers: [], projectErrors: {} });
            this.getProjects();
        } catch (e) {
            console.error('[MapaRevendaLocacaoMapbox] erro ao salvar projeto:', e);
            this.setState({ projectErrors: e?.response?.data?.errors || {} });
            toast.error('Erro ao salvar o projeto.');
        } finally {
            this.setState({ savingProject: false });
        }
    };

    deleteProject = async () => {
        const id = this.state.filters.projeto;
        if (!id) return;
        const nome = this.state.projeto_name.find((o) => o.value === id)?.label || 'selecionado';
        if (!window.confirm(`Excluir o projeto "${nome}"?`)) return;
        try {
            const res = await axios.delete(route('api.projetos-revenda-locacao-mapbox.destroy', { projeto: id }));
            toast.success(res.data?.message || 'Projeto excluído.');
            this.cleanState();
            this.getProjects();
        } catch (e) {
            console.error('[MapaRevendaLocacaoMapbox] erro ao excluir projeto:', e);
            toast.error('Erro ao excluir o projeto.');
        }
    };

    parseJson = (value, fallback = null) => {
        if (value == null) return fallback;
        if (typeof value !== 'string') return value;
        try { return JSON.parse(value) ?? fallback; } catch { return fallback; }
    };

    handleProjetoChange = async (id) => {
        if (!id) return;
        this.cleanState();
        try {
            const res = await axios.get(route('api.projetos-revenda-locacao-mapbox.show', { projeto: id }));
            const p = res.data?.project;
            if (!p) return;

            const tipoNegocioArr = this.parseJson(p.tipo_negocio, []);
            const tipo_negocio = tipoNegocioArr[0]?.value || 'Venda';
            const cidadeArr = this.parseJson(p.cidade, []);
            const estadoArr = this.parseJson(p.estado, []);
            const cidadeOption = cidadeArr[0] || null;

            const bairrosOptions = cidadeOption ? await this.fetchCrawlerBairros(cidadeOption.label) : [];

            const filters = {
                ...EMPTY_FILTERS,
                projeto: id,
                tipo_negocio,
                tipo_empreendimento: this.parseJson(p.tipo, []),
                estadosSelecionados: estadoArr[0] || (cidadeOption ? cidadeOption.value.split('-')[0] : null),
                estado: estadoArr.length ? estadoArr : (cidadeOption ? [cidadeOption.value.split('-')[0]] : []),
                cidade: cidadeOption ? [cidadeOption] : [],
                bairros: this.parseJson(p.bairros, []),
                bairrosOptions: bairrosOptions || [],
                area_maior_que: p.area_maior_que || null,
                area_menor_que: p.area_menor_que || null,
                valor_maior_que: p.valor_maior_que || null,
                valor_menor_que: p.valor_menor_que || null,
                valor_metro_maior_que: p.valor_metro_maior_que || null,
                valor_metro_menor_que: p.valor_metro_menor_que || null,
                quartos: this.parseJson(p.quartos, []),
                vagas: this.parseJson(p.vagas, []),
            };

            await new Promise((resolve) => this.setState({ filters }, resolve));

            await new Promise((resolve) => this.setState({
                projectName: p.name || '',
                projectShareUsers: res.data?.share_users || [],
            }, resolve));

            await this.addProjectShapes(this.parseJson(p.polignos, []), this.parseJson(p.manualCircles, []));
            await this.reloadEmpreendimentos();
            toast.success('Projeto carregado.');
        } catch (e) {
            console.error('[MapaRevendaLocacaoMapbox] erro ao carregar projeto:', e);
            toast.error('Erro ao carregar o projeto.');
        }
    };

    addProjectShapes = async (polygons, circles) => {
        if (!this.draw) return;
        const newShapes = [];
        const allBounds = new mapboxgl.LngLatBounds();

        const register = (drawId, name, type, style) => {
            const idx = this.state.drawnShapes.length + newShapes.length;
            const color = (style.color || style.fillColor) || colors[idx % colors.length];
            const areaId = `drawn-${Date.now()}-${idx}`;
            const styleAttrs = {
                fillColor: color, fillOpacity: style.fillOpacity ?? 0.3,
                strokeColor: style.strokeColor || '#000000', strokeWeight: style.strokeWeight ?? 3,
            };
            Object.entries(styleAttrs).forEach(([k, v]) => this.draw.setFeatureProperty(drawId, k, v));
            const feature = this.draw.get(drawId);
            newShapes.push({
                drawId, areaId, type, name: name || `${type} ${idx + 1}`, color, visible: true, feature,
                center: feature?.properties?.center || null, radiusMeters: feature?.properties?.radiusMeters || null, ...styleAttrs,
            });
        };

        (polygons || []).forEach((data) => {
            const path = data?.polygon ?? data;
            if (!Array.isArray(path) || path.length < 3) return;
            const ring = path.map(([lat, lng]) => [lng, lat]);
            const first = ring[0];
            const last = ring[ring.length - 1];
            if (first[0] !== last[0] || first[1] !== last[1]) ring.push([...first]);
            const [drawId] = this.draw.add({ type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [ring] } });
            register(drawId, data?.name, 'Polígono', data || {});
            ring.forEach((c) => allBounds.extend(c));
        });

        (circles || []).forEach((data) => {
            if (!data) return;
            const lat = parseFloat(data.center?.lat ?? data.latitude);
            const lng = parseFloat(data.center?.lng ?? data.longitude);
            if (!Number.isFinite(lat) || !Number.isFinite(lng) || !data.radius) return;
            const feature = makeCircleFeature([lng, lat], data.radius, 'circle');
            const [drawId] = this.draw.add(feature);
            register(drawId, data.name, data.type || 'Círculo', data);
            (feature.geometry.coordinates[0] || []).forEach((c) => allBounds.extend(c));
        });

        if (newShapes.length > 0) {
            await new Promise((resolve) => this.setState((prev) => ({ drawnShapes: [...prev.drawnShapes, ...newShapes] }), resolve));
            if (!allBounds.isEmpty()) this.map.fitBounds(allBounds, { padding: 60, duration: 600 });
        }
    };

    // ===== Limpar =====
    cleanState = () => {
        this.addressMarker = removeAddressMarker(this.addressMarker);
        this.searchContext = null;
        this.lastTileKey = null;
        this.markerAbort?.abort();
        clearTimeout(this.moveTimer);
        this.currentGeojson = null;
        this.allFeatures = [];
        this.draw?.deleteAll();
        this.map?.getSource(SOURCE_ID)?.setData(EMPTY_FC);
        this.map?.getSource(BAIRROS_SOURCE_ID)?.setData(EMPTY_FC);
        this.deactivateRuler();
        const zoneamentoLimpo = zoneamento.resetZoneamento(this);
        const datalayerLimpo = datalayer.resetDatalayer(this);
        if (this.searchInputRef.current) this.searchInputRef.current.value = '';
        this.setState({
            filters: { ...EMPTY_FILTERS },
            drawnShapes: [],
            searchAreas: [],
            right_panel_visible: false,
            totalEmpreendimentos: 0,
            loadingClusters: false,
            showBairros: false,
            bairrosAvailable: false,
            showEmpreendimentos: true,
            agruparMarcadores: true,
            showAlertCity: false,
            showImovelModal: false,
            imovelDetail: null,
            projectName: '',
            projectShareUsers: [],
            projectErrors: {},
            ...zoneamentoLimpo,
            ...datalayerLimpo,
            ...isochrone.ISOCHRONE_INITIAL_STATE,
        });
    };

    // ===== Busca por endereço (Google Places) =====
    setupGooglePlaces = () => {
        loadGoogleMapsPlaces()
            .then(() => {
                if (!this.searchInputRef.current) return;
                const autocomplete = new window.google.maps.places.Autocomplete(this.searchInputRef.current, {
                    // `types` alimenta o marcador do endereço: ele só aparece para um ponto
                    // específico, nunca para uma cidade. Ver MapboxMap/features/addressMarker.js.
                    fields: ['geometry', 'formatted_address', 'name', 'types'],
                    componentRestrictions: { country: 'br' },
                });
                autocomplete.addListener('place_changed', async () => {
                    const place = autocomplete.getPlace();
                    if (!place.geometry?.location) return;
                    const lng = place.geometry.location.lng();
                    const lat = place.geometry.location.lat();
                    this.map.flyTo({ center: [lng, lat], zoom: SELECTED_ZOOM, essential: true });
                    this.addressMarker = syncAddressMarker(this.map, this.addressMarker, place, lat, lng);
                    await this.loadFromAddress(lat, lng, [place]);
                });
                this.searchInputRef.current.addEventListener('keydown', (e) => { if (e.key === 'Enter') e.preventDefault(); });
            })
            .catch((err) => console.error('[MapaRevendaLocacaoMapbox] falha ao carregar Google Places:', err));
    };

    loadFromAddress = async (lat, lng, place) => {
        let reverse;
        try { reverse = await geolocationService.reverse(lat, lng, place); }
        catch { toast.error('Não foi possível identificar a localidade.'); return; }

        const uf = reverse?.uf;
        const cidade = reverse?.cidade;
        if (!uf || !cidade) { toast.warn('Localidade sem cidade/UF identificável.'); return; }

        const cidadeOption = { value: `${uf}-${cidade}`, label: cidade };
        const bairrosOptions = await this.fetchCrawlerBairros(cidade);
        await new Promise((resolve) => this.setState((prev) => ({
            filters: { ...prev.filters, cidade: [cidadeOption], estado: [uf], estadosSelecionados: uf, bairros: [], bairrosOptions: bairrosOptions || [] },
        }), resolve));

        this.refreshBairrosAvailability([cidadeOption]);
        this.refreshZoneamentoAvailability([cidadeOption]);
        this.loadYearsOptionsForLinkagesDataLayer([cidadeOption]);
        await this.reloadEmpreendimentos();
        if (this.searchInputRef.current) this.searchInputRef.current.value = '';
    };

    render() {
        const { left_side_bar_visible, right_panel_visible, active_tab, searchAreas, showImovelModal } = this.state;

        /*
         * Barras laterais fora do caminho enquanto um diálogo grande está aberto.
         * É CSS, e não a prop `visible`: a Sidebar do PrimeReact desmonta o conteúdo ao
         * fechar, o que zeraria os filtros e a lista de formas a cada abertura.
         */
        const barrasOcultas = showImovelModal
            || this.state.show_map_capture
            || this.state.show_modal_customizar_cores_setores
            || this.state.show_modal_data_dictionary
            || this.state.show_modal_datalayer_datatable;

        return (
            <>
                <div ref={this.containerRef} style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }} />

                <input
                    ref={this.searchInputRef}
                    type="text"
                    placeholder="Digite um endereço"
                    className="mrl-mapbox-search"
                />

                {(this.state.loading || this.state.loadingClusters) && (
                    <div className="mapbox-layer-loading" role="status">
                        <i className="fa fa-spin fa-spinner" /> Carregando imóveis…
                    </div>
                )}

                <LayerLoadingIndicator labels={this.state.layerLoading} />

                {/* Aleta lateral: a mesma abre e fecha a barra, acompanhando a borda dela
                    (substitui o "x" da Sidebar e o antigo botão de reabrir). */}
                {!barrasOcultas && (
                    <button
                        type="button"
                        className={`mapbox-flap mapbox-flap-left${left_side_bar_visible ? ' is-open' : ''}`}
                        title={left_side_bar_visible ? 'Ocultar barra lateral' : 'Mostrar barra lateral'}
                        aria-expanded={left_side_bar_visible}
                        onClick={() => this.setState({ left_side_bar_visible: !left_side_bar_visible })}
                    >
                        <i className={`fas ${left_side_bar_visible ? 'fa-angle-double-left' : 'fa-angle-double-right'}`} />
                    </button>
                )}

                <Sidebar
                    visible={left_side_bar_visible}
                    onHide={() => this.setState({ left_side_bar_visible: false })}
                    modal={false}
                    dismissable={false}
                    showCloseIcon={false}
                    className={`mapbox-sidebar${barrasOcultas ? ' mapbox-sidebar-oculta' : ''}`}
                >
                    <Tabs activeKey={active_tab} onSelect={(e) => this.setState({ active_tab: e })} id="mrl-mapbox-tabs">
                        <Tab eventKey="filtros" title="Filtros">
                            <div style={{ paddingRight: '6px', paddingLeft: '6px' }}>
                            <div className="card no-border-radius" style={{ borderRadius: '0 !important' }}>
                            <div className="card-body" style={{ height: '96vh', overflowY: 'auto', marginTop: '10px' }}>
                                <FormFiltersRevenda
                                    filters={this.state.filters}
                                    projetoOptions={this.state.projeto_name}
                                    hasAreas={this.state.drawnShapes.length > 0}
                                    showAlertCity={this.state.showAlertCity}
                                    onChange={this.handleFilterChange}
                                    onToggleQuarto={this.handleToggleQuarto}
                                    onToggleVaga={this.handleToggleVaga}
                                    onEstadoChange={this.handleEstadoChange}
                                    onCidadeChange={this.handleCidadeChange}
                                    onProjetoChange={this.handleProjetoChange}
                                    onSubmit={this.reloadEmpreendimentos}
                                    onClear={this.cleanState}
                                />
                            </div>
                            </div>
                            </div>
                        </Tab>

                        <Tab eventKey="ferramentas" title="Ferramentas">
                            <div style={{ paddingRight: '6px', paddingLeft: '6px' }}>
                            <div className="card no-border-radius" style={{ borderRadius: '0 !important' }}>
                            <div className="card-body" style={{ height: '96vh', overflowY: 'auto', marginTop: '10px' }}>
                                <div className="form-group" title="A preferência fica salva neste navegador.">
                                    <label className="mb-2 d-block">Mapa base</label>
                                    <Dropdown
                                        value={this.state.mapStyle}
                                        onChange={(e) => this.setMapStyle(e.value)}
                                        options={MAP_STYLE_OPTIONS}
                                        optionLabel="label"
                                        appendTo={document.body}
                                        style={{ width: '100%' }}
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="mb-2 d-block">Exibir no mapa</label>
                                    <div className="mapbox-tools-switches">
                                        <div className="d-flex align-items-center">
                                            <InputSwitch
                                                inputId="check_imoveis"
                                                checked={this.state.showEmpreendimentos}
                                                onChange={(e) => this.toggleEmpreendimentos(e.value)}
                                            />
                                            <label htmlFor="check_imoveis" className="pl-1 mb-0 ms-2">Imóveis</label>
                                        </div>

                                        <div
                                            className="d-flex align-items-center"
                                            title="Desligado, cada imóvel aparece como um marcador próprio, sem agrupar por proximidade."
                                        >
                                            <InputSwitch
                                                inputId="check_agrupar_marcadores"
                                                checked={this.state.agruparMarcadores}
                                                onChange={(e) => this.toggleAgruparMarcadores(e.value)}
                                            />
                                            <label htmlFor="check_agrupar_marcadores" className="pl-1 mb-0 ms-2">
                                                Agrupar marcadores
                                            </label>
                                        </div>
                                        {this.state.bairrosAvailable && (
                                            <div className="d-flex align-items-center">
                                                <InputSwitch
                                                    inputId="check_bairros"
                                                    checked={this.state.showBairros}
                                                    onChange={(e) => this.handleToggleBairros(e.value)}
                                                />
                                                <label htmlFor="check_bairros" className="pl-1 mb-0 ms-2">Limite de bairros</label>
                                            </div>
                                        )}
                                        {CAN_VER_ZONEAMENTO && this.state.zoneamentoAvailable && (
                                            <div className="d-flex align-items-center">
                                                <InputSwitch
                                                    inputId="check_zoneamento"
                                                    checked={this.state.showZoneamento}
                                                    onChange={(e) => this.toggleShowZoneamento(e.value)}
                                                />
                                                <label htmlFor="check_zoneamento" className="pl-1 mb-0 ms-2">Zoneamento</label>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {this.state.drawnShapes.length > 0 && (
                                    <div>
                                        <hr />
                                        <DrawnShapesList
                                            shapes={this.state.drawnShapes}
                                            onCenter={this.centerOnShape}
                                            onToggleVisibility={this.toggleShapeVisibility}
                                            onSetRadius={this.setShapeRadius}
                                            onEditAttributes={this.editShapeAttributes}
                                            onRemove={this.removeShape}
                                        />
                                    </div>
                                )}

                                <SociodemografiaTools
                                    permissoes={this.state.permissoesSociodemografia}
                                    datalayers={this.state.datalayers}
                                    datalayerName={this.state.datalayerName}
                                    datalayerColorProperty={this.state.datalayerColorProperty}
                                    datalayerColorPropertyOptions={this.state.datalayerColorPropertyOptions}
                                    datalayerYear={this.state.datalayerYear}
                                    datalayerYears={this.state.datalayerYears}
                                    datalayerYearOptions={this.state.datalayerYearOptions}
                                    showDemarcacaoAreas={this.state.show_demarcacao_areas}
                                    loading={this.state.loadingDatalayer}
                                    requerPropriedade={datalayer.camadaRequerPropriedadeParaColorirMapa(this)}
                                    requerAno={datalayer.camadaRequerAno(this)}
                                    requerMultiplosAnos={datalayer.camadaRequerMultiplosAnos(this)}
                                    onChangeName={(e) => datalayer.onChangeDatalayerName(this, e)}
                                    onChangeColorProperty={(e) => datalayer.onChangeDatalayerColorProperty(this, e)}
                                    onChangeYear={(e) => datalayer.onChangeDatalayerYear(this, e)}
                                    onChangeYears={(e) => datalayer.onChangeDatalayerYears(this, e)}
                                    onLoadDataLayer={() => datalayer.loadDataLayer(this)}
                                    onChangeDemarcacaoAreas={(v) => datalayer.setDemarcacaoAreas(this, v)}
                                    onOpenDataDictionary={() => this.setState({ show_modal_data_dictionary: true })}
                                    onOpenDataTable={() => datalayer.loadDataLayerAggregated(this)}
                                />

                                <hr />

                                <div className="form-group">

                                    <div className="mapbox-tools-actions">
                                        <button type="button" className="btn btn-default btn-block" onClick={() => this.setState({ modalSalvarProjetoOpen: true })}>
                                            Salvar Projeto
                                        </button>
                                        {this.state.filters.projeto && (
                                            <button type="button" className="btn btn-sm btn-danger" onClick={this.deleteProject}>
                                                Excluir Projeto
                                            </button>
                                        )}
                                        <button type="button" className="btn btn-sm btn-default bold rem10" onClick={this.handleDownloadKml} disabled={this.state.loadingKml}>
                                            Exportar KML
                                        </button>
                                        <button type="button" className="btn btn-sm btn-default bold rem10" onClick={() => this.kmlInputRef.current?.click()} disabled={this.state.loadingKml}>
                                            Importar KML/KMZ
                                        </button>
                                        <button type="button" className="btn btn-default btn-block" onClick={this.printMap} title="Efetua Captura de Tela do Mapa">
                                            Capturar
                                        </button>
                                    </div>
                                    <input
                                        ref={this.kmlInputRef}
                                        type="file"
                                        accept=".kml,.kmz"
                                        style={{ display: 'none' }}
                                        onChange={(e) => { this.handleImportKmlFile(e.target.files?.[0]); e.target.value = ''; }}
                                    />
                                    {this.state.loadingKml && <small className="text-muted d-block mt-2">Processando arquivo…</small>}

                                    <div style={{marginBottom: '50px'}}></div>
                                </div>
                            </div>
                            </div>
                            </div>
                        </Tab>

                        <Tab eventKey="legendas" title="Legenda">
                            <div style={{ paddingRight: '6px', paddingLeft: '6px' }}>
                            <div className="card no-border-radius" style={{ borderRadius: '0 !important' }}>
                            <div className="card-body" style={{ height: '96vh', overflowY: 'auto', marginTop: '10px' }} id="legendas-container">
                                {this.state.showZoneamento && this.state.legendaZoneamento.length > 0 && (
                                    <LegendaDataLayer legendas={this.state.legendaZoneamento} />
                                )}
                                {this.state.showLegendaRenda && (
                                    <RendaIbgeLegend
                                        openModalLegendas={() => this.setState({ show_modal_customizar_cores_setores: true })}
                                        colorRanges={this.state.color_ranges[this.getColorRangeKey()]}
                                        title={this.state.datalayerFriendlyName}
                                        datalayerName={this.state.datalayerName}
                                        datalayerColorProperty={this.state.datalayerColorProperty}
                                    />
                                )}
                                <Legenda />
                            </div>
                            </div>
                            </div>
                        </Tab>
                    </Tabs>
                </Sidebar>

                {searchAreas.length > 0 && !barrasOcultas && (
                    <button
                        type="button"
                        className={`mapbox-flap mapbox-flap-right${right_panel_visible ? ' is-open' : ''}`}
                        title={right_panel_visible ? 'Ocultar resultados' : 'Mostrar resultados'}
                        aria-expanded={right_panel_visible}
                        onClick={() => this.setState({ right_panel_visible: !right_panel_visible })}
                    >
                        <i className={`fas ${right_panel_visible ? 'fa-angle-double-right' : 'fa-chart-bar'}`} />
                        {!right_panel_visible && (
                            <span className="mapbox-flap-badge">{searchAreas.length}</span>
                        )}
                    </button>
                )}

                {/* A largura vem do CSS (--mapbox-right-panel-width) para a aleta poder
                    acompanhar a borda do painel. */}
                <Sidebar
                    visible={right_panel_visible}
                    position="right"
                    onHide={() => this.setState({ right_panel_visible: false })}
                    modal={false}
                    dismissable={false}
                    showCloseIcon={false}
                    className={`mapbox-right-panel${barrasOcultas ? ' mapbox-sidebar-oculta' : ''}`}
                >
                    <div className="mapbox-right-panel-header">
                        <strong>Áreas selecionadas</strong>
                        <span className="badge badge-secondary ml-2">{searchAreas.length}</span>
                    </div>
                    <div className="mapbox-right-panel-body">
                        <ResultsPanel
                            areas={searchAreas}
                            onExportExcel={this.handleExportExcel}
                            onShowPolos={this.handleShowPolos}
                            onRemoveArea={this.removeArea}
                        />
                    </div>
                </Sidebar>

                <ImovelModal
                    show={this.state.showImovelModal}
                    loading={this.state.loadingImovel}
                    empreendimento={this.state.imovelDetail}
                    onHide={this.closeImovelModal}
                />

                <ShapeAttributesEditor
                    show={this.state.shapeEditorOpen}
                    shape={this.state.shapeEditing}
                    onHide={this.closeShapeEditor}
                    onApply={(attrs) => this.applyShapeAttributes(this.state.shapeEditing, attrs)}
                />

                <ModalPolosGeradores
                    show={this.state.show_modal_polos_geradores}
                    handleCloseModal={() => this.setState({ show_modal_polos_geradores: false })}
                    polos={this.state.polos_geradores_current_showing}
                    polos_geradores_agrupados={this.state.polos_geradores_current_grouped}
                    cacheKey={this.state.polos_geradores_current_cache_key}
                />

                <ModalSalvarProjeto
                    show={this.state.modalSalvarProjetoOpen}
                    handleClose={() => this.setState({ modalSalvarProjetoOpen: false, projectErrors: {} })}
                    project_name={this.state.projectName}
                    handleNameProjectChanged={(e) => this.setState({ projectName: e.target.value })}
                    share_users={this.state.projectShareUsers}
                    handleShareUsersChanged={(users) => this.setState({ projectShareUsers: users || [] })}
                    errors={this.state.projectErrors}
                    loading={this.state.savingProject}
                    saveProject={this.saveProject}
                />

                <MapCapture
                    visible={this.state.show_map_capture}
                    handleClose={this.closeMapCapture}
                    imageData={this.state.mapCaptureData}
                    canvas={this.state.mapCaptureCanvas}
                />

                <RaioDeslocamentoModal
                    show={this.state.showRaioDeslocamento}
                    center={this.state.isochroneCenter}
                    onHide={() => this.setState({ showRaioDeslocamento: false })}
                    onIsochronesReady={this.handleIsochronesReady}
                />

                <LinkagesDataDictionaryModal
                    visible={this.state.show_modal_data_dictionary}
                    onHide={() => this.setState({ show_modal_data_dictionary: false })}
                    activeDatalayer={this.state.datalayerName}
                />

                {this.state.show_modal_datalayer_datatable && (
                    <LinkagesDataTableModal
                        visible={this.state.show_modal_datalayer_datatable}
                        onHide={() => this.setState({ show_modal_datalayer_datatable: false })}
                        activeDatalayer={this.state.datalayerName}
                        overlays={this.overlaysForDatalayerTable()}
                        data={this.state.datalayers_table_data}
                        getTransformedPayload={() => this.buildSensoPayload()}
                    />
                )}

                <ModalLegendaRendaIbge
                    show={this.state.show_modal_customizar_cores_setores}
                    handleClose={() => this.setState({ show_modal_customizar_cores_setores: false })}
                    colorRanges={this.state.color_ranges[this.getColorRangeKey()]}
                    updateRange={(colorRange, index) => datalayer.updateRendaColorRange(this, colorRange, index)}
                    replaceRanges={(ranges) => datalayer.replaceColorRanges(this, ranges)}
                    removeRange={(index) => datalayer.removeRendaColorRange(this, index)}
                    handleAplyColorRanges={() => datalayer.handleAplyColorRanges(this)}
                    colorOptions={this.state.colorsOptions}
                    datalayerName={this.state.datalayerFriendlyName}
                    datalayerColorProperty={this.state.datalayerColorProperty}
                    datalayerFillOpacity={this.state.datalayerFillOpacity}
                    onChangeOpacidadePreenchimento={(e, v) => datalayer.onChangeOpacidadePreenchimento(this, e, v)}
                />

                <Toast ref={this.toastRef} position="top-right" />
            </>
        );
    }
}

export default MapaRevendaLocacaoMapbox;

if (document.getElementById('mapa-revenda-locacao-mapbox')) {
    ReactDOM.render(<MapaRevendaLocacaoMapbox />, document.getElementById('mapa-revenda-locacao-mapbox'));
}
